import React, { useState, useEffect } from 'react';
import { retailersAPI, kycAPI } from '../services/api';
import './AgentPages.css';

const RetailerRegistration = () => {
  const [formData, setFormData] = useState({
    shopName: '',
    name: '',
    email: '',
    phone: '',
    address: '',
    panNumber: '',
    aadhaarNumber: '',
  });
  const [panFile, setPanFile] = useState(null);
  const [aadhaarFile, setAadhaarFile] = useState(null);
  const [electricityFile, setElectricityFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [retailers, setRetailers] = useState([]);

  // KYC Verification States
  const [showAadhaarModal, setShowAadhaarModal] = useState(false);
  const [showPanModal, setShowPanModal] = useState(false);
  const [aadhaarVerificationStep, setAadhaarVerificationStep] = useState('send-otp'); // 'send-otp' or 'verify-otp'
  const [aadhaarOtpData, setAadhaarOtpData] = useState({ refId: null, verificationId: null });
  const [aadhaarOtp, setAadhaarOtp] = useState('');
  const [panVerificationStep, setPanVerificationStep] = useState('verify'); // Only one step
  const [kycLoading, setKycLoading] = useState(false);
  const [kycError, setKycError] = useState(null);
  const [verificationStatus, setVerificationStatus] = useState({
    aadhaar: { verified: false, message: '' },
    pan: { verified: false, message: '' },
  });

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (type === 'PAN') setPanFile(file);
    if (type === 'AADHAAR') setAadhaarFile(file);
    if (type === 'ELECTRICITY_BILL') setElectricityFile(file);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // ===== Aadhaar Verification Handlers =====
  const handleAadhaarSendOtp = async () => {
    if (!formData.aadhaarNumber || !/^\d{12}$/.test(formData.aadhaarNumber.replace(/\s/g, ''))) {
      setKycError('Please enter a valid 12-digit Aadhaar number');
      return;
    }

    try {
      setKycLoading(true);
      setKycError(null);
      const response = await kycAPI.sendAadhaarOtp(formData.aadhaarNumber.replace(/\s/g, ''));
      const respData = response?.data || response;

      setAadhaarOtpData({
        refId: respData.refId || respData.ref_id || null,
        verificationId: respData.verificationId || respData.verification_id || null,
      });
      setAadhaarVerificationStep('verify-otp');
      setSuccess('OTP sent successfully! Check your registered mobile number.');
    } catch (err) {
      setKycError(err.message || 'Failed to send OTP. Please try again.');
    } finally {
      setKycLoading(false);
    }
  };

  const handleAadhaarVerifyOtp = async () => {
    if (!aadhaarOtp || !/^\d{6}$/.test(aadhaarOtp)) {
      setKycError('Please enter a valid 6-digit OTP');
      return;
    }

    try {
      setKycLoading(true);
      setKycError(null);
      const response = await kycAPI.verifyAadhaarOtp(
        formData.aadhaarNumber.replace(/\s/g, ''),
        aadhaarOtp,
        aadhaarOtpData.refId
      );
      const respData = response?.data || response;

      const aadhaarMasked = respData.aadhaar || respData.aadhar || respData.aadhar_no || '';

      setVerificationStatus(prev => ({
        ...prev,
        aadhaar: { 
          verified: true, 
          message: `✓ Verified: ${aadhaarMasked}` 
        },
      }));
      setSuccess('Aadhaar verified successfully!');
      setShowAadhaarModal(false);
      setAadhaarVerificationStep('send-otp');
      setAadhaarOtp('');
    } catch (err) {
      console.warn('Aadhaar verify error:', err);
      setKycError(formatKycError(err) || 'Failed to verify OTP. Please try again.');
    } finally {
      setKycLoading(false);
    }
  };

  const handleAadhaarResendOtp = async () => {
    setAadhaarVerificationStep('send-otp');
    setAadhaarOtp('');
    await handleAadhaarSendOtp();
  };

  // ===== PAN Verification Handlers =====
  const handlePanVerify = async () => {
    if (!formData.panNumber || !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(formData.panNumber)) {
      setKycError('Please enter a valid PAN (e.g., ABCDE1234F)');
      return;
    }

    try {
      setKycLoading(true);
      setKycError(null);
      const response = await kycAPI.verifyPan(formData.panNumber);
      const result = response.data || response; // Handle both wrapped and direct responses
      
      setVerificationStatus(prev => ({
        ...prev,
        pan: { 
          verified: true, 
          message: `✓ ${result.panNo} - ${result.registeredName}` 
        },
      }));
      setSuccess('PAN verified successfully!');
      setShowPanModal(false);
    } catch (err) {
      console.warn('PAN verify error:', err);
      setKycError(formatKycError(err) || 'Failed to verify PAN. Please try again.');
    } finally {
      setKycLoading(false);
    }
  };

  // Helper to map API errors to user-friendly KYC messages
  const formatKycError = (err) => {
    if (!err) return null;
    // If apiCall threw an Error with numeric status
    const status = err.status || (err.response && err.response.status) || null;
    const msg = (err.data && err.data.message) || err.message || '';

    if (status === 502) return 'Verification service is temporarily unavailable. Please try again later.';
    if (status === 503) return 'Verification service is not configured. Please contact support.';
    if (status === 401 || status === 403) return 'Authentication error. Please sign in again.';
    // Common upstream messages from verification provider
    if (msg && /authentication/i.test(msg)) return 'Verification provider authentication failed. Please check configuration.';

    return msg || null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic frontend validation
    if (!formData.name || !formData.shopName || !formData.phone || !formData.email || !formData.address || !formData.panNumber || !formData.aadhaarNumber) {
      setError('Please fill all required fields');
      return;
    }

    if (formData.phone && !/^\d{10}$/.test(formData.phone)) {
      setError('Phone number must be exactly 10 digits');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(formData.panNumber.toUpperCase())) {
      setError('Please enter a valid PAN (e.g., ABCDE1234F)');
      return;
    }

    if (!/^\d{12}$/.test(formData.aadhaarNumber.replace(/\s/g, ''))) {
      setError('Please enter a valid 12-digit Aadhaar number');
      return;
    }

    if (!panFile || !aadhaarFile || !electricityFile) {
      setError('Please upload PAN, Aadhaar, and Electricity Bill documents');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      // Map frontend fields to backend expected field names
      const payload = {
        name: formData.name.trim(),
        shopName: formData.shopName.trim(),  
        phone: formData.phone.trim(),
        email: formData.email?.trim() || null,
        address: formData.address?.trim() || null,
        panNumber: formData.panNumber?.trim() || null,
        aadhaarNumber: formData.aadhaarNumber?.trim() || null,
      };

      const response = await retailersAPI.register(payload);
      const retailer = response.data || response;
      if (!retailer.id) throw new Error('Retailer registration failed');
      // Upload documents if present
      setUploading(true);
      const uploadPromises = [];
      if (panFile) {
        const panForm = new FormData();
        panForm.append('document', panFile);
        panForm.append('documentType', 'PAN');
        uploadPromises.push(retailersAPI.uploadDocument(retailer.id, panForm));
      }
      if (aadhaarFile) {
        const aadhaarForm = new FormData();
        aadhaarForm.append('document', aadhaarFile);
        aadhaarForm.append('documentType', 'AADHAAR');
        uploadPromises.push(retailersAPI.uploadDocument(retailer.id, aadhaarForm));
      }
      if (electricityFile) {
        const elecForm = new FormData();
        elecForm.append('document', electricityFile);
        elecForm.append('documentType', 'ELECTRICITY_BILL');
        uploadPromises.push(retailersAPI.uploadDocument(retailer.id, elecForm));
      }
      await Promise.all(uploadPromises);
      setSuccess('Retailer registered and documents uploaded successfully!');
      setFormData({
        shopName: '',
        name: '',
        email: '',
        phone: '',
        address: '',
        panNumber: '',
        aadhaarNumber: '',
      });
      setPanFile(null);
      setAadhaarFile(null);
      setElectricityFile(null);
      // Refresh retailers list so newly created retailer appears
      await fetchRetailers();
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      setError(err.message || 'Failed to register retailer or upload documents.');
    } finally {
      setLoading(false);
      setUploading(false);
    }
  };

  const fetchRetailers = async () => {
    try {
      const list = await retailersAPI.list();
      // retailersAPI.list may return response.data or array depending on backend
      let arr = [];
      if (Array.isArray(list)) arr = list;
      else if (list && Array.isArray(list.data)) arr = list.data;
      else if (list && Array.isArray(list.retailers)) arr = list.retailers;
      setRetailers(arr);
    } catch (err) {
      // ignore fetch errors silently for now
    }
  };

  useEffect(() => {
    fetchRetailers();
  }, []);

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Register New Retailer</h1>
        <p>Add a new retailer to your network</p>
      </div>

      <div className="form-container">
        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label>Shop Name *</label>
              <input
                type="text"
                name="shopName"
                value={formData.shopName}
                onChange={handleInputChange}
                required
                placeholder="e.g., ABC Retail Store"
              />
            </div>
            <div className="form-group">
              <label>Owner Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                placeholder="e.g., Rahul Sharma"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Phone Number *</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                required
                placeholder="10-digit mobile number"
              />
            </div>
            <div className="form-group">
              <label>Email *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                placeholder="business@example.com"
              />
            </div>
          </div>


          <div className="form-row">
            <div className="form-group">
              <label>PAN Number *</label>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                  type="text"
                  name="panNumber"
                  value={formData.panNumber}
                  onChange={handleInputChange}
                  required
                  placeholder="ABCDE1234F"
                />
                <button
                  type="button"
                  onClick={() => setShowPanModal(true)}
                  className="button button-small button-secondary"
                  disabled={!formData.panNumber || kycLoading}
                  title="Verify PAN with backend"
                >
                  {verificationStatus.pan.verified ? '✓ Verified' : 'Verify'}
                </button>
              </div>
              {verificationStatus.pan.verified && (
                <small style={{ color: 'green' }}>{verificationStatus.pan.message}</small>
              )}
            </div>
            <div className="form-group">
              <label>Aadhaar Number *</label>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                  type="text"
                  name="aadhaarNumber"
                  value={formData.aadhaarNumber}
                  onChange={handleInputChange}
                  required
                  placeholder="12-digit Aadhaar"
                />
                <button
                  type="button"
                  onClick={() => setShowAadhaarModal(true)}
                  className="button button-small button-secondary"
                  disabled={!formData.aadhaarNumber || kycLoading}
                  title="Verify Aadhaar via OTP"
                >
                  {verificationStatus.aadhaar.verified ? '✓ Verified' : 'Verify'}
                </button>
              </div>
              {verificationStatus.aadhaar.verified && (
                <small style={{ color: 'green' }}>{verificationStatus.aadhaar.message}</small>
              )}
            </div>
          </div>

          {/* Removed city, state, pincode, businessType fields as not present in DB/backend */}

          <div className="form-group">
            <label>Address *</label>
            <textarea
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              required
              placeholder="Complete business address"
            />
          </div>

          {/* Document Uploads */}
          <div className="form-row">
            <div className="form-group">
              <label>PAN Document *</label>
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={e => handleFileChange(e, 'PAN')}
                required
              />
              {panFile && <small>Selected: {panFile.name}</small>}
            </div>
            <div className="form-group">
              <label>Aadhaar Document *</label>
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={e => handleFileChange(e, 'AADHAAR')}
                required
              />
              {aadhaarFile && <small>Selected: {aadhaarFile.name}</small>}
            </div>
            <div className="form-group">
              <label>Electricity Bill *</label>
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={e => handleFileChange(e, 'ELECTRICITY_BILL')}
                required
              />
              {electricityFile && <small>Selected: {electricityFile.name}</small>}
            </div>
          </div>
          <div className="form-actions">
            <button type="submit" className="button button-primary" disabled={loading || uploading}>
              {loading || uploading ? 'Processing...' : 'Register Retailer'}
            </button>
            <button
              type="reset"
              className="button button-secondary"
              onClick={() => {
                setFormData({
                  shopName: '',
                  name: '',
                  email: '',
                  phone: '',
                  address: '',
                  panNumber: '',
                  aadhaarNumber: '',
                });
                setPanFile(null);
                setAadhaarFile(null);
                setElectricityFile(null);
              }}
            >
              Clear Form
            </button>
          </div>
        </form>
      </div>

      <div className="list-container" style={{ marginTop: 20 }}>
        <h2>Your Retailers</h2>
        {retailers.length === 0 ? (
          <div className="no-data">No retailers yet.</div>
        ) : (
          <div className="table-wrapper">
            <table className="table">
              <thead>
                <tr>
                  <th>Shop Name</th>
                  <th>Owner</th>
                  <th>Phone</th>
                  <th>Email</th>
                </tr>
              </thead>
              <tbody>
                {retailers.map(r => (
                  <tr key={r.id}>
                    <td>{r.shop_name || r.shopName || r.name}</td>
                    <td>{r.name || `${r.first_name || ''} ${r.last_name || ''}`}</td>
                    <td>{r.phone}</td>
                    <td>{r.email}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Aadhaar Verification Modal */}
      {showAadhaarModal && (
        <div className="modal-overlay" onClick={() => setShowAadhaarModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Verify Aadhaar</h2>
              <button
                type="button"
                className="modal-close"
                onClick={() => {
                  setShowAadhaarModal(false);
                  setAadhaarVerificationStep('send-otp');
                  setAadhaarOtp('');
                  setKycError(null);
                }}
              >
                ✕
              </button>
            </div>

            <div className="modal-body">
              {kycError && <div className="alert alert-error">{kycError}</div>}

              {aadhaarVerificationStep === 'send-otp' ? (
                <div>
                  <p>Enter your 12-digit Aadhaar number to receive an OTP:</p>
                  <div className="form-group">
                    <label>Aadhaar Number</label>
                    <input
                      type="text"
                      value={formData.aadhaarNumber}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 12);
                        setFormData(prev => ({ ...prev, aadhaarNumber: val }));
                      }}
                      placeholder="XXXX XXXX XXXX"
                      maxLength="12"
                    />
                    <small>You will receive OTP on your registered mobile number</small>
                  </div>
                </div>
              ) : (
                <div>
                  <p>Enter the 6-digit OTP sent to your registered mobile number:</p>
                  <div className="form-group">
                    <label>OTP</label>
                    <input
                      type="text"
                      value={aadhaarOtp}
                      onChange={(e) => setAadhaarOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                      maxLength="6"
                      inputMode="numeric"
                    />
                    <small>
                      Didn't receive? {' '}
                      <button
                        type="button"
                        onClick={handleAadhaarResendOtp}
                        style={{ background: 'none', border: 'none', color: '#007bff', cursor: 'pointer', textDecoration: 'underline' }}
                        disabled={kycLoading}
                      >
                        Resend OTP
                      </button>
                    </small>
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="button button-secondary"
                onClick={() => {
                  setShowAadhaarModal(false);
                  setAadhaarVerificationStep('send-otp');
                  setAadhaarOtp('');
                  setKycError(null);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="button button-primary"
                onClick={aadhaarVerificationStep === 'send-otp' ? handleAadhaarSendOtp : handleAadhaarVerifyOtp}
                disabled={kycLoading}
              >
                {kycLoading ? 'Processing...' : (aadhaarVerificationStep === 'send-otp' ? 'Send OTP' : 'Verify OTP')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PAN Verification Modal */}
      {showPanModal && (
        <div className="modal-overlay" onClick={() => setShowPanModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Verify PAN</h2>
              <button
                type="button"
                className="modal-close"
                onClick={() => {
                  setShowPanModal(false);
                  setKycError(null);
                }}
              >
                ✕
              </button>
            </div>

            <div className="modal-body">
              {kycError && <div className="alert alert-error">{kycError}</div>}

              <p>Enter your PAN number to verify:</p>
              <div className="form-group">
                <label>PAN</label>
                <input
                  type="text"
                  value={formData.panNumber}
                  onChange={(e) => {
                    const val = e.target.value.toUpperCase().slice(0, 10);
                    setFormData(prev => ({ ...prev, panNumber: val }));
                  }}
                  placeholder="ABCDE1234F"
                  maxLength="10"
                />
                <small>Format: 5 letters, 4 digits, 1 letter (e.g., ABCDE1234F)</small>
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="button button-secondary"
                onClick={() => {
                  setShowPanModal(false);
                  setKycError(null);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="button button-primary"
                onClick={handlePanVerify}
                disabled={kycLoading}
              >
                {kycLoading ? 'Verifying...' : 'Verify PAN'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RetailerRegistration;
